/* eslint-disable no-useless-escape */
/**
 * Shared calculation helper functions for grading calculations
 * Extracted from Calculation.js for reuse across modules
 */

/**
 * Whether a stored value is one of the tokens meaning "this size was not measured", and so
 * takes no part in a calculation.
 *
 * The comparison is an exact match against the token list, trimmed and case-folded, mirroring
 * the server helper of the same name byte for byte. It used to be a substring test, which made
 * the two ends disagree about real values: `"12/34"` and `"NaN"` were skips on the client and
 * numbers on the server, while `" -"` — a skip token with one stray space, as a paste or import
 * produces — was a skip on the server and a rule of 0 on the client. A cell the two ends classify
 * differently is calculated one way on screen and stored another (BM-9510).
 * @param {*} val - The value to check
 * @returns {boolean} - True if value should be skipped
 */
export const isSkipValue = (val) => {
  if (!val && val !== 0) return false;
  const str = String(val).toLowerCase().trim();
  return ['n/a', 'na', 'n', 'a', '/', '-'].includes(str);
};

/**
 * Reads a stored grade rule into a magnitude plus the direction its sign encodes.
 *
 * Kept byte-for-byte in step with the server helper of the same name: the two ends calculate the
 * same stored rule, so any difference between them shows up as the screen and the database
 * disagreeing about a measurement. An unparseable rule yields a neutral result rather than a
 * signed zero, and only a LEADING sign is stripped — stripping every sign in the string
 * concatenated the digits either side of an interior one, so a malformed `5-3` parsed as the
 * magnitude 53 here and as 5 on the server (BM-9510).
 * @param {string|number} gradingStr - The grading string/number to parse
 * @param {boolean} isLeftSide - True when the size sits left of the base size
 * @returns {{value: number, shouldAdd: boolean}} Magnitude and the direction to apply it in
 */
export const parseGradingValue = (gradingStr, isLeftSide = false) => {
  if (!gradingStr && gradingStr !== 0) return { value: 0, shouldAdd: false };
  if (isSkipValue(gradingStr)) return { value: 0, shouldAdd: false };

  const str = gradingStr.toString().trim();
  const numericValue = parseFloat(str.replace(/^[+-]/, ''));

  if (Number.isNaN(numericValue)) {
    return { value: 0, shouldAdd: false };
  }
  /**
   * Direction the sign encodes, mirroring the server helper of the same name so both ends of the
   * spec calculation read a stored rule identically. A column's position already implies a
   * direction — left of the base reduces, right of it adds — so an unsigned rule follows its
   * position and the ordinary ascending size run is unaffected. Only a sign that CONTRADICTS the
   * position flips the branch, which is the case position alone cannot express: a left-of-base
   * size measuring larger than the base, or a right-of-base size measuring smaller.
   *
   * `shouldAdd` was never returned here, so BulkEdit's measurement-calc.ts — which destructures it
   * and drives its +/- branch off it — read `undefined` and subtracted for every size, including
   * those right of the base that must add (BM-9510).
   */
  const hasExplicitPlus = str.startsWith('+');
  const hasExplicitMinus = str.startsWith('-');
  const shouldAdd = isLeftSide ? hasExplicitPlus : !hasExplicitMinus;

  return { value: Math.abs(numericValue), shouldAdd };
};

/**
 * Input validation regex pattern that accepts numbers, +/- signs, decimals, and skip values
 * Enhanced version of existing specNumberRegex to include + sign
 * Standardized pattern for keyPress validation across all grading components
 */
export const GRADING_INPUT_PATTERN = /[0-9+\-.sNAna/]/;

/**
 * Enhanced regex for complete grading value validation
 * Based on existing specNumRegex but includes + sign support
 * Allows: +/-123.456, N/A, n/a, NA, na, N, A, n, a, /, -, +
 */
export const ENHANCED_GRADING_REGEX = /^[+-]?\d{1,4}(\.\d{0,3})?$|^[NAna\/+-]$|^[Nn]\/[Aa]$|^[Nn][Aa]$/;

/**
 * Validates if grading input is in correct format
 * Enhanced version that works with existing validation functions
 * @param {string} input - The input string to validate
 * @returns {boolean} - True if input is valid
 */
export const isValidGradingInput = (input) => {
  if (!input || input === '') return true; // Empty input is valid

  const str = input.toString().trim();

  if (isSkipValue(str)) return true;

  const signMatches = str.match(/[+-]/g);
  if (signMatches && signMatches.length > 1) {
    return isSkipValue(str);
  }

  return ENHANCED_GRADING_REGEX.test(str);
};

/**
 * Enhanced decimal validation that works with signed values
 * @param {string} value - The value to validate
 * @returns {boolean} - True if decimal places are <= 3
 */
export const checkIs3DigitDecimalSigned = (value) => {
  if (!value) return true;
  const cleanValue = value.toString().replace(/^[+-]/, '');
  const len = cleanValue.split('.')[1]?.length || 0;
  return len <= 3;
};

/**
 * Enhanced integer part validation that works with signed values
 * @param {string} value - The value to validate
 * @returns {boolean} - True if integer part is <= 4 digits
 */
export const check4DigitDecimalSigned = (value) => {
  if (value == null) return false;
  const cleanValue = value.toString().replace(/^[+-]/, '');
  const [integerPart] = cleanValue.split('.');
  return integerPart.length <= 4;
};

/**
 * Module types for different display formatting requirements
 */
export const MAX_GRADING_DECIMALS = 3;

/**
 * Counts the decimal places a value was written with, ignoring any leading sign and
 * capping at the 3 places the grading validator accepts.
 * @param {string|number|null|undefined} value raw grading or measurement
 * @returns {number}
 */
export const decimalPlacesOf = (value) => {
  const decimals = String(value ?? '')
    .trim()
    .replace(/^[+-]/, '')
    .split('.')[1];
  return decimals ? Math.min(decimals.length, MAX_GRADING_DECIMALS) : 0;
};

/**
 * Serialises a derived measurement with as many decimals as it or its inputs carried, so
 * a grading of '1.500' yields '-0.500' instead of the number -0.5 — trailing zeros cannot
 * survive a JS number. Including the computed value itself means padding to the grading
 * never rounds a digit the arithmetic actually produced.
 * @param {number} value computed measurement
 * @param {...(string|number|null|undefined)} sources raw inputs it was derived from
 * @returns {string}
 */
export const formatDerivedMeasurement = (value, ...sources) =>
  value.toFixed(Math.max(decimalPlacesOf(value), ...sources.map(decimalPlacesOf)));

/**
 * Prints a grade rule or tolerance with the decimals it was written with, capped at
 * MAX_GRADING_DECIMALS. `toFixed` on its own pads every value out to three places, and a padded
 * value fed back into a controlled input is what stopped the user ever leaving a rule at fewer
 * decimals — a typed '2' was rewritten to '2.000' on the render after the keypress (BM-9884).
 *
 * An explicit '+' is kept because it encodes the rule's direction, which parseGradingValue reads
 * back; a sign the user did not type is never added. A skip token is returned as written.
 * @param {string|number|null|undefined} value raw grade rule or tolerance
 * @returns {string} the value without padding, or '' when there is nothing to print
 */
export const trimGradingDecimals = (value) => {
  if (!value && value !== 0) return '';
  const numericValue = parseFloat(value);
  if (Number.isNaN(numericValue)) return String(value);
  const hasExplicitPlus = String(value).trim().startsWith('+');
  const trimmed = numericValue.toFixed(MAX_GRADING_DECIMALS).replace(/\.?0+$/, '');
  return hasExplicitPlus && numericValue > 0 ? `+${trimmed}` : trimmed;
};

/**
 * Drops the zeros that trail a measurement's decimals, so a value that landed on a whole number
 * prints as one: '4.0' and '4.000' both read '4', while '4.50' keeps its value as '4.5'.
 *
 * A derived measurement is padded to the widest decimal count among its inputs, which is what
 * stops a rule of '1.500' degrading to 0.5. That padding is right for the stored value and wrong
 * for the printed one, so it is undone here rather than at the point of calculation.
 *
 * The '.' guard matters: the regex alone would read the final zero of '10' as a trailing decimal
 * zero and print '1'.
 * @param {string|number|null|undefined} value stored measurement
 * @returns {string} the value with redundant trailing zeros removed
 */
export const trimTrailingZeros = (value) => {
  const str = String(value ?? '');
  if (!str.includes('.')) return str;
  const trimmed = str.replace(/\.?0+$/, '');
  return Number(trimmed) === 0 ? '0' : trimmed;
};

export const MODULE_TYPES = {
  TECHPACK: 'techpack',
  GRADING: 'grading',
  PRODUCT_SPEC: 'product_spec',
  SAMPLES: 'samples',
  SPEC_TEMPLATE: 'spec_template',
};

/**
 * Formats display value for different modules
 * @param {*} value - The value to format
 * @param {boolean} hideSigns - Whether to hide +/- signs (for techpack)
 * @returns {string} - Formatted display value
 */
export const formatDisplayValue = (value, hideSigns = false) => {
  if (isSkipValue(value)) return value;

  if (hideSigns && value) {
    const str = value.toString();
    return str.replace(/^[+-]/, '');
  }

  return value;
};

/**
 * Formats measurement for display (alias for formatDisplayValue)
 * @param {*} value - The value to format
 * @param {boolean} hideSigns - Whether to hide +/- signs
 * @returns {string} - Formatted measurement display value
 */
export const formatMeasurementForDisplay = (value, hideSigns = false) => {
  return formatDisplayValue(value, hideSigns);
};

/**
 * Enhanced display formatter for module-specific requirements
 * @param {*} value - The value to format
 * @param {string} moduleType - The module type (from MODULE_TYPES)
 * @param {object} options - Additional formatting options
 * @returns {string} - Formatted display value
 */
export const formatValueForModule = (value, moduleType = MODULE_TYPES.GRADING, options = {}) => {
  if (!value && value !== 0) return '';

  if (isSkipValue(value)) return value.toString();

  const { precision = 3, showPositiveSign = false, emptyValue = '' } = options;

  let displayValue = value.toString();

  switch (moduleType) {
    case MODULE_TYPES.TECHPACK:
      displayValue = displayValue.replace(/^[+-]/, '');
      break;

    case MODULE_TYPES.GRADING:
    case MODULE_TYPES.PRODUCT_SPEC:
    case MODULE_TYPES.SAMPLES:
    case MODULE_TYPES.SPEC_TEMPLATE:
      if (showPositiveSign && !displayValue.startsWith('+') && !displayValue.startsWith('-')) {
        displayValue = `+${displayValue}`;
      }
      break;

    default:
      break;
  }

  if (precision && !isNaN(parseFloat(displayValue.replace(/^[+-]/, '')))) {
    const sign = displayValue.match(/^[+-]/)?.[0] || '';
    const numValue = parseFloat(displayValue.replace(/^[+-]/, ''));
    const formattedNum = numValue.toFixed(precision).replace(/\.?0+$/, '');
    displayValue = sign + formattedNum;
  }

  return displayValue || emptyValue;
};

/**
 * Formats input value for storage/calculation
 * @param {*} inputValue - The raw input value
 * @param {string} moduleType - The module type
 * @returns {string} - Formatted value for storage
 */
export const formatValueForStorage = (inputValue, moduleType = MODULE_TYPES.GRADING) => {
  if (!inputValue && inputValue !== 0) return '';

  if (isSkipValue(inputValue)) return inputValue.toString();

  const cleanValue = inputValue.toString().trim();

  if (moduleType === MODULE_TYPES.TECHPACK) {
    return cleanValue;
  }

  return cleanValue;
};

/**
 * Formats calculated measurement result
 * @param {number} calculatedValue - The calculated numeric result
 * @param {string} moduleType - The module type
 * @param {object} options - Formatting options
 * @returns {string} - Formatted measurement display value
 */
export const formatCalculatedMeasurement = (calculatedValue, moduleType = MODULE_TYPES.GRADING, options = {}) => {
  if (calculatedValue === null || calculatedValue === undefined || Number.isNaN(calculatedValue)) {
    return '';
  }

  const { precision = 3, removeTrailingZeros = true } = options;

  let result = calculatedValue.toFixed(precision);

  if (removeTrailingZeros) {
    result = result.replace(/\.?0+$/, '');
  }

  if (moduleType === MODULE_TYPES.TECHPACK) {
    result = Math.abs(calculatedValue).toFixed(precision);
    if (removeTrailingZeros) {
      result = result.replace(/\.?0+$/, '');
    }
  }

  return result;
};

/**
 * Formats grading input for display in input fields
 * @param {*} gradingValue - The grading value
 * @param {boolean} isZeroSize - Whether this is the zero/sample size
 * @param {boolean} isSampleSize - Whether this is a sample size
 * @param {boolean} hideSigns - Whether to hide signs (for techpack)
 * @returns {string} - Formatted grading display value
 */
export const formatGradingForDisplay = (gradingValue, isZeroSize = false, isSampleSize = false, hideSigns = false) => {
  if (isZeroSize || isSampleSize) {
    return '0';
  }

  if (!gradingValue && gradingValue !== 0) return '';

  return formatDisplayValue(gradingValue, hideSigns);
};

/**
 * Enhanced version of isNumberOrSpecial that supports module-specific formatting
 * Based on the existing CardDragSpec.js implementation with added sign hiding support
 * @param {*} val - The value to format
 * @param {boolean} hasIsSampleKey - Whether this is a sample size
 * @param {boolean} shouldRound - Whether to round numeric values
 * @param {boolean} hasIsZeroKey - Whether this is a zero size
 * @param {string} moduleType - The module type for specific formatting
 * @returns {*} - Formatted display value
 */
export const enhancedNumberOrSpecial = (val, hasIsSampleKey = false, shouldRound = false, hasIsZeroKey = false, moduleType = MODULE_TYPES.GRADING) => {
  if (val === undefined || val === null || val === '') return '';

  // If flagged as zero/sample and value is not a number (but not empty), return 0
  if ((hasIsZeroKey || hasIsSampleKey) && val !== '' && Number.isNaN(Number(val))) return 0;

  // Allow in-progress decimal typing like ".", "2.", "+", "+2.", "-2." without coercing to a number
  if (typeof val === 'string') {
    const str = val;
    if (str === '.' || str === '+' || str === '-' || /^[+-]?\d*\.$/.test(str)) {
      // For techpack, hide signs even during typing
      if (moduleType === MODULE_TYPES.TECHPACK && (str === '+' || str === '-' || str.startsWith('+') || str.startsWith('-'))) {
        return str.replace(/^[+-]/, '');
      }
      return str;
    }
    // For grading fields (no rounding), preserve exact string to keep signs/cursor
    if (!shouldRound) {
      if (moduleType === MODULE_TYPES.TECHPACK) {
        return formatValueForModule(str, moduleType);
      }
      return str;
    }
  }

  const parsed = typeof val === 'string' ? Number(val) : val;
  const isNumeric = typeof parsed === 'number' && Number.isFinite(parsed);

  if (isNumeric && shouldRound && !hasIsZeroKey && !hasIsSampleKey) {
    const rounded = parsed.toFixed(3).replace(/\.?0+$/, '');
    return formatValueForModule(rounded, moduleType);
  }

  if (isNumeric) {
    return formatValueForModule(parsed, moduleType);
  }

  return val;
};

/**
 * Creates module-specific display formatters
 * @param {string} moduleType - The module type
 * @returns {object} - Object with formatting functions for the module
 */
export const createModuleFormatters = (moduleType) => {
  return {
    forInput: (value, isZeroSize = false, isSampleSize = false) => enhancedNumberOrSpecial(value, isSampleSize, false, isZeroSize, moduleType),
    forMeasurement: (value, isZeroSize = false, isSampleSize = false) => enhancedNumberOrSpecial(value, isSampleSize, true, isZeroSize, moduleType),
    forGrading: (value, isZeroSize = false, isSampleSize = false) => enhancedNumberOrSpecial(value, isSampleSize, false, isZeroSize, moduleType),
    forCalculatedResult: (value, options = {}) => formatCalculatedMeasurement(value, moduleType, options),
    forStorage: (value) => formatValueForStorage(value, moduleType),
  };
};

export const TECHPACK_FORMATTERS = createModuleFormatters(MODULE_TYPES.TECHPACK);
export const GRADING_FORMATTERS = createModuleFormatters(MODULE_TYPES.GRADING);
export const PRODUCT_SPEC_FORMATTERS = createModuleFormatters(MODULE_TYPES.PRODUCT_SPEC);
export const SAMPLES_FORMATTERS = createModuleFormatters(MODULE_TYPES.SAMPLES);
export const SPEC_TEMPLATE_FORMATTERS = createModuleFormatters(MODULE_TYPES.SPEC_TEMPLATE);

/**
 * Validates input on keyPress events
 * @param {KeyboardEvent} e - The keyboard event
 * @returns {boolean} - True if key should be allowed
 */
export const validateGradingKeyPress = (e) => {
  return GRADING_INPUT_PATTERN.test(e.key);
};

/**
 * Enhanced onChange handler for grading inputs
 * Combines existing validation logic with new signed value support
 * @param {string} value - The input value
 * @returns {boolean} - True if value should be accepted
 */
export const validateGradingOnChange = (value) => {
  if (!value || value === '') return true;

  const val = value.toString().trim();

  // Allow skip values
  if (isSkipValue(val)) return true;

  // Allow incomplete input (just + or - sign)
  if (val === '+' || val === '-') return true;

  // Validate using enhanced validation
  return isValidGradingInput(val) && checkIs3DigitDecimalSigned(val) && check4DigitDecimalSigned(val);
};

/**
 * Creates a keyPress event handler with enhanced validation
 * @param {object} options - Configuration options
 * @param {boolean} options.allowSigns - Whether to allow +/- signs (default: true)
 * @returns {function} - KeyPress event handler
 */
export const createGradingKeyPressHandler = (options = {}) => {
  const { allowSigns = true } = options;

  return (e) => {
    let allowed = /[0-9.\sNA/na-]/;
    if (allowSigns) {
      allowed = /[0-9+-.\\sNA/na]/;
    }

    if (!allowed.test(e.key)) {
      e.preventDefault();
    }
  };
};

/**
 * Creates an onChange event handler with enhanced validation
 * @param {function} setValue - Function to set the value
 * @param {object} options - Configuration options
 * @param {boolean} options.allowSigns - Whether to allow +/- signs (default: true)
 * @returns {function} - OnChange event handler
 */
export const createGradingChangeHandler = (setValue, options = {}) => {
  const { allowSigns = true } = options;

  return (e) => {
    const val = e.target.value;

    if (!allowSigns) {
      // Strip signs for modules that shouldn't show them
      const cleanVal = val.replace(/^[+-]/, '');
      if (validateGradingOnChange(cleanVal)) {
        setValue(cleanVal);
      }
    } else if (validateGradingOnChange(val)) {
      setValue(val);
    }
  };
};

export const validateIntermediateGradingInput = (val) => {
  return val === '' || /^[+-]?\d*\.?$/.test(val) || /^[+-]?\d*\.\d*$/.test(val) || /^[NAna/-]$/.test(val) || /^[Nn]\/?[Aa]?$/.test(val);
};
